/**
 * HTTP client para a API PrevDrive.
 *
 * Guarda access/refresh token em
 * localStorage, injeta o Bearer automaticamente e faz refresh silencioso em
 * 401 (uma única vez, para evitar loop).
 */

const API_URL = (import.meta.env.VITE_API_URL as string | undefined) ?? '';
const ACCESS_KEY = 'prevdrive-access-token';
const REFRESH_KEY = 'prevdrive-refresh-token';
const USER_KEY = 'prevdrive-user';

export interface StoredUser {
  id: string;
  email: string;
  name?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
  }
}

/**
 * Backend disponível para o cliente?
 *
 * Sempre verdadeiro no deploy atual: com proxy same-origin (VITE_API_URL vazio,
 * que é o padrão), as requisições usam caminhos relativos resolvidos contra a
 * própria página e o nginx roteia /api e /ws até a API. Com VITE_API_URL
 * explícita, aponta para outra origem. Em ambos os casos há um backend por trás
 * — não existe mais o modo "sem backend" (local-only) da era Supabase.
 */
export function isCloudConfigured(): boolean {
  return true;
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function getStoredUser(): StoredUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as StoredUser;
  } catch {
    return null;
  }
}

export function storeSession(user: StoredUser, accessToken: string, refreshToken: string): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
  localStorage.setItem(ACCESS_KEY, accessToken);
  localStorage.setItem(REFRESH_KEY, refreshToken);
}

export function clearSession(): void {
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

interface RequestOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  /** Desativa a tentativa automática de refresh (usado pelo próprio refresh). */
  noRetry?: boolean;
}

async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, noRetry, headers, ...rest } = options;

  const requestHeaders: Record<string, string> = {
    ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(headers as Record<string, string> | undefined),
  };

  const token = getAccessToken();
  if (token) requestHeaders.Authorization = `Bearer ${token}`;

  const response = await fetch(`${API_URL}${path}`, {
    ...rest,
    headers: requestHeaders,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (response.status === 401 && !noRetry) {
    // Tenta refresh uma única vez; se falhar, propaga 401 para o chamador.
    const refreshed = await tryRefresh();
    if (refreshed) {
      return rawRequest<T>(path, { ...options, noRetry: true });
    }
  }

  const text = await response.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const err = parsed as { error?: string; message?: string } | null;
    throw new ApiError(
      response.status,
      err?.error ?? 'error',
      err?.message ?? `Erro ${response.status}`,
    );
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

let refreshPromise: Promise<boolean> | null = null;

/**
 * Refresh de sessão. Usa uma promise compartilhada para que requisições
 * concorrentes disparem um único refresh (evita thundering herd).
 */
export async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return false;

    try {
      const response = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });

      if (!response.ok) {
        clearSession();
        return false;
      }

      const data = (await response.json()) as {
        accessToken: string;
        refreshToken: string;
      };

      localStorage.setItem(ACCESS_KEY, data.accessToken);
      localStorage.setItem(REFRESH_KEY, data.refreshToken);
      return true;
    } catch {
      return false;
    } finally {
      // Libera para o próximo refresh após 1s (dá tempo das chamadas em voo usarem).
      setTimeout(() => {
        refreshPromise = null;
      }, 1000);
    }
  })();

  return refreshPromise;
}

export async function apiGet<T>(path: string, options?: RequestOptions): Promise<T> {
  return rawRequest<T>(path, { ...options, method: 'GET' });
}

export async function apiPost<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T> {
  return rawRequest<T>(path, { ...options, method: 'POST', body });
}

export async function apiDelete<T>(path: string, options?: RequestOptions): Promise<T> {
  return rawRequest<T>(path, { ...options, method: 'DELETE' });
}
